import React, { type JSX, ComponentType, createElement, MouseEventHandler } from "react";
import { twMerge, twJoin } from "tailwind-merge";
import { updateClassName } from "../../../utils/mixed";

import { SupText as Text } from "../../../components/UI/Text";
import { CommonColors } from "../../../types/colors";

export interface ChipProps {
  variant?: CommonColors;
  icon?: string | JSX.Element;
  subIcon?: string | JSX.Element;
  label?: string | JSX.Element;
  subLabel?: string | JSX.Element;
  border?: boolean;
  borderStyle?: "solid" | "dashed";
  type?: "rounded" | "square";
  iconType?: "rounded" | "square";
  position?: "left" | "right";
  small?: boolean;
  disabled?: boolean;
  active?: boolean;
  tabIndex?: number;
  iconComponent?: "img" | ComponentType<{ src: string; className?: string }>;
  inactive?: boolean;
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

const CHIP_VARIANTS = {
  primary: "text-neutral-900 [&.active]:text-neutral-700 [&.active]:bg-neutral-300",
  success: "text-success-800 [&.active]:text-success-900 [&.active]:bg-success-50",
  error: "text-error-800  [&.active]:text-error-925 [&.active]:bg-error-50",
  info: "text-info-500 [&.active]:text-info-800 [&.active]:bg-info-50",
  warning: "text-warning-600 [&.active]:text-warning-900 [&.active]:bg-warning-50",
} as const;

const CHIP_CLICKABLE_VARIANTS = {
  primary: "cursor-pointer hover:text-neutral-700 focus-visible:text-neutral-700 focus-visible:bg-neutral-300 active:text-neutral-700 active:bg-neutral-800",
  success: "cursor-pointer hover:text-success-700 focus-visible:text-success-800 focus-visible:bg-success-25 active:text-success-800 active:bg-success-50",
  error: "cursor-pointer hover:text-error-800 focus-visible:text-error-900 focus-visible:bg-error-25 active:text-error-925 active:bg-error-50",
  info: "cursor-pointer hover:text-info-600 focus-visible:text-info-700 focus-visible:bg-info-25 active:text-info-800 active:bg-info-50",
  warning: "cursor-pointer hover:text-warning-700 focus-visible:text-warning-800 focus-visible:bg-warning-25 active:text-warning-900 active:bg-warning-50",
} as const;

export function SupChip({
  variant = "primary",
  className,
  icon,
  subIcon,
  label,
  subLabel,
  tabIndex,
  border = false,
  borderStyle = "solid",
  type = "rounded",
  iconType = "rounded",
  position = "left",
  small = false,
  active = false,
  inactive = false,
  disabled = false,
  iconComponent = "img",
  onClick,
}: ChipProps) {
  const iconElement =
    typeof icon === "string"
      ? createElement(iconComponent, { src: icon, className: twMerge("object-cover object-center w-full h-full", inactive && "grayscale") })
      : icon
        ? updateClassName(icon, twMerge("object-cover object-center w-full h-full", inactive && "opacity-50"))
        : icon;
  const subIconElement =
    typeof subIcon === "string"
      ? createElement(iconComponent, { src: subIcon, className: twMerge("object-cover object-center w-full h-full", inactive && "grayscale") })
      : subIcon
        ? updateClassName(subIcon, twMerge("object-cover object-center w-full h-full", inactive && "opacity-50"))
        : subIcon;

  return (
    <div
      className={twMerge(
        "group/chip inline-flex items-center py-[4px] border transition-colors duration-200 ",
        CHIP_VARIANTS[variant],
        active && !disabled && "active",
        disabled && "disabled [&.disabled]:cursor-default [&.disabled]:bg-neutral-25 [&.disabled]:border-neutral-100",
        position === "right" && Boolean(label) && "pl-3 pr-1",
        position === "left" && Boolean(label) && "pr-3 pl-1",
        position === "left" && "flex-row",
        position === "right" && "flex-row-reverse",
        type === "rounded" && "rounded-full",
        !border ? "border-transparent" : "border-current",
        borderStyle === "solid" && "border-solid",
        borderStyle === "dashed" && "border-dashed",
        Boolean(onClick) && !disabled && CHIP_CLICKABLE_VARIANTS[variant],
        inactive && border && "border-neutral-100",
        className
      )}
      onClick={!disabled ? onClick : undefined}
      tabIndex={!disabled ? tabIndex : undefined}
    >
      {Boolean(icon) && (
        <div className="relative group-[.disabled]/chip:opacity-16">
          {Boolean(icon) && <div className={twJoin(iconType === "rounded" && "rounded-full overflow-hidden", small ? "h-6 w-6" : "h-9 w-9")}>{iconElement}</div>}
          {Boolean(subIcon) && (
            <div
              className={twJoin(
                "absolute bottom-0",
                iconType === "rounded" && "rounded-full overflow-hidden",
                position === "left" && "right-0",
                position === "right" && "left-0",
                small ? "h-3 w-3" : "h-4 w-4"
              )}
            >
              {subIconElement}
            </div>
          )}
        </div>
      )}

      {Boolean(label) && (
        <div className={twJoin("group-[.disabled]/chip:text-neutral-500 min-w-0", position === "left" && "ml-2 text-left", position === "right" && "mr-2 text-right")}>
          {typeof label === "string" ? (
            <Text variant={small ? "caption" : "button"} className="whitespace-nowrap">
              {label}
            </Text>
          ) : (
            label
          )}
          {Boolean(subLabel) &&
            (typeof subLabel === "string" ? (
              <Text variant="body2" className={twJoin("uppercase whitespace-nowrap", !small && "mt-0")}>
                {subLabel}
              </Text>
            ) : (
              subLabel
            ))}
        </div>
      )}
    </div>
  );
}
